import { Button as MuiButton } from '@mui/material';
import { Drawer, Form, Input } from 'antd';
import LunarDatePicker from 'components/LunarDatePicker';
import { DEFAULT_OVERLAY_WIDTH } from 'components/Overlays/constants';
import useOverlayPanelHelper from 'components/Overlays/useOverlayPanelHelper';
import {
  useGetPeakQuery,
  useUpdatePeakMutation,
} from 'features/configuration/query';
import {
  PEAK_SEASON_TYPE,
  TPeakSeasonConfig,
} from 'features/configuration/types';
import { Dispatch, memo, useCallback, useEffect, useMemo } from 'react';
import { messageUtils } from 'utils';
import useGetDisableDateRange from 'utils/hooks/useGetDisableDateRange';
import dayjs from 'utils/moment/dayjsConfig';
import { ISO8601_FORMAT } from 'utils/moment/utils';
import { FORM_FIELD, FormLabel, FormValidation } from './constants';

type PeakSeasonAddEditPanelProps = {
  open: boolean;
  setOpen: Dispatch<boolean>;
  selectedPeakSeasonItem?: TPeakSeasonConfig;
};

const { useForm, useWatch } = Form;

const PeakSeasonAddEditPanel = ({
  open,
  setOpen,
  selectedPeakSeasonItem,
}: PeakSeasonAddEditPanelProps) => {
  const { code } = selectedPeakSeasonItem || {};

  const { message } = messageUtils;

  const panelTitle = useMemo(
    () => (code ? 'Sửa thông tin' : 'Thêm cao điểm bán'),
    [code]
  );

  const [form] = useForm();
  const { setFieldsValue, resetFields } = form;

  const selectedStartDate = useWatch(FORM_FIELD.START_FROM, form);
  const selectedEndDate = useWatch(FORM_FIELD.END_TO, form);

  const { disabledFromDate, disabledToDate } = useGetDisableDateRange(
    selectedStartDate,
    selectedEndDate
  );

  const { data: getPeakResponse } = useGetPeakQuery(
    {
      code: code ?? '',
    },
    {
      skip: !code || !open,
    }
  );

  const [updatePeakMutationFn, { isLoading, isSuccess }] =
    useUpdatePeakMutation();

  const onClose = useCallback(() => {
    resetFields();
    setOpen(false);
  }, [setOpen, resetFields]);

  const { overlayPanelFooter, submitBtnRef } = useOverlayPanelHelper({
    cancelBtnProps: { onClick: onClose },
    submitBtnProps: {
      loading: isLoading,
      label: code ? 'Lưu thay đổi' : 'Thêm cao điểm bán',
    },
  });

  const initialValues = useMemo(() => {
    const { startFrom, endTo, description, name } = getPeakResponse?.data || {};

    return {
      [FORM_FIELD.NAME]: name ?? '',
      [FORM_FIELD.DESCRIPTION]: description ?? '',
      [FORM_FIELD.START_FROM]: startFrom
        ? dayjs(startFrom, ISO8601_FORMAT)
        : undefined,
      [FORM_FIELD.END_TO]: endTo ? dayjs(endTo, ISO8601_FORMAT) : undefined,
    };
  }, [getPeakResponse?.data]);

  useEffect(() => {
    open && setFieldsValue(initialValues);
  }, [initialValues, setFieldsValue, open]);

  const onSubmit = useCallback(
    (values: TPeakSeasonConfig) => {
      try {
        const payload = {
          ...values,
          startFrom: dayjs(values.startFrom).format(ISO8601_FORMAT),
          endTo: dayjs(values.endTo).format(ISO8601_FORMAT),
          type: PEAK_SEASON_TYPE.CUSTOMIZE,
          ...(code && { code }),
        };

        updatePeakMutationFn([payload]);
      } catch (e) {}
    },
    [code, updatePeakMutationFn]
  );

  useEffect(() => {
    if (!isSuccess) return;
    void message.success(`${code ? 'Cập nhật' : 'Thêm'} thành công`);
    onClose();
  }, [code, isSuccess, message, onClose]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={panelTitle}
      footer={overlayPanelFooter}
      width={DEFAULT_OVERLAY_WIDTH}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name={FORM_FIELD.NAME}
          label={FormLabel[FORM_FIELD.NAME]}
          rules={FormValidation[FORM_FIELD.NAME]}
        >
          <Input placeholder={FormLabel[FORM_FIELD.NAME]} />
        </Form.Item>

        <Form.Item
          name={FORM_FIELD.DESCRIPTION}
          label={FormLabel[FORM_FIELD.DESCRIPTION]}
        >
          <Input placeholder={FormLabel[FORM_FIELD.DESCRIPTION]} />
        </Form.Item>

        <Form.Item
          name={FORM_FIELD.START_FROM}
          label={FormLabel[FORM_FIELD.START_FROM]}
          className="flex-1"
          rules={FormValidation[FORM_FIELD.START_FROM]}
        >
          <LunarDatePicker disabledDate={disabledFromDate} />
        </Form.Item>

        <Form.Item
          name={FORM_FIELD.END_TO}
          label={FormLabel[FORM_FIELD.END_TO]}
          className="flex-1"
          rules={FormValidation[FORM_FIELD.END_TO]}
        >
          <LunarDatePicker disabledDate={disabledToDate} />
        </Form.Item>

        <MuiButton type="submit" className="hidden" ref={submitBtnRef}>
          Xác nhận
        </MuiButton>
      </Form>
    </Drawer>
  );
};

export default memo(PeakSeasonAddEditPanel);
